import React from "react";
import { SectionLink } from "./SectionLink";

/**
 * Macro-balance section — the triangle is the hero. Each food is a point in
 * protein/fat/carb space by calorie-share; the shaded oval is the balanced
 * range (the Dietary Guidelines' macro split, drawn soft because both the
 * guideline and our values are estimates). TrueNooch lands inside it on the
 * lean, low-fat side; every comparison food — including edamame — falls out.
 * Colors are tuned for the site's dark background (#0d1b2a / cream text).
 */

const foods: { x: number; y: number; label: string; anchor?: "start" | "middle" | "end"; dx?: number; dy?: number }[] = [
  { x: 274, y: 112, label: "chicken", dx: 6, dy: -4 },
  { x: 223, y: 112, label: "seitan", anchor: "end", dx: -5, dy: -6 },
  { x: 208, y: 146, label: "greek yogurt", anchor: "end", dx: -6, dy: -2 },
  { x: 313, y: 239, label: "tofu", dx: 7, dy: -2 },
  { x: 346, y: 258, label: "egg", dx: 7, dy: -2 },
  { x: 364, y: 283, label: "cheddar", dx: 7, dy: -2 },
  { x: 339, y: 310, label: "peanut butter", anchor: "end", dx: -6, dy: -1 },
  { x: 291, y: 310, label: "hummus", anchor: "end", dx: -5, dy: 12 },
  { x: 357, y: 345, label: "avocado", dx: 7, dy: 7 },
  { x: 124, y: 286, label: "black beans", anchor: "end", dx: -6, dy: -2 },
  { x: 143, y: 314, label: "quinoa", dx: 7, dy: -6 },
  { x: 133, y: 326, label: "corn", anchor: "end", dx: -7, dy: 11 },
  { x: 90, y: 335, label: "rice", anchor: "end", dx: -6, dy: -2 },
  { x: 247, y: 252, label: "edamame", dx: 7, dy: -3 },
];

export function MacroBalanced() {
  return (
    <section className="section fade-in" id="macro-section" style={{ paddingTop: "1.5rem" }}>
      <div className="section-label">Naturally balanced</div>
      <h2>Balanced macros, without the mental overhead</h2>
      <p>
        Most foods are mostly one thing — mostly fat, mostly carbs, or mostly protein. TrueNooch™ is a
        bit of everything, in the range dietitians call balanced.
      </p>

      <div style={{ maxWidth: 480, margin: "1.75rem auto 0.75rem" }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 18, justifyContent: "center", fontSize: 13.5, color: "#D3DAE2", marginBottom: 10 }}>
          <span style={{ display: "flex", alignItems: "center", gap: 6, fontWeight: 600 }}>
            <span style={{ width: 14, height: 14, borderRadius: "50%", background: "#5DBE3A" }} />
            TrueNooch
          </span>
          <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ width: 16, height: 12, borderRadius: 6, background: "#5DBE3A", opacity: 0.4, border: "1px solid #5DBE3A" }} />
            balanced range
          </span>
          <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ width: 11, height: 11, borderRadius: "50%", background: "#8b93a0" }} />
            everything else
          </span>
        </div>

        <svg
          viewBox="0 0 500 405"
          width="100%"
          role="img"
          aria-label="Ternary macro plot with a soft balanced-range oval; TrueNooch inside on the lean side, all other foods including edamame outside."
          style={{ fontFamily: "inherit" }}
        >
          <polygon points="240,50 70,360 410,360" fill="none" stroke="#54637A" strokeWidth="1" />
          <ellipse cx="194" cy="296" rx="37" ry="46" fill="#5DBE3A" fillOpacity="0.16" stroke="#5DBE3A" strokeOpacity="0.5" strokeWidth="1.25" strokeDasharray="4 4" />
          <text x="194" y="324" textAnchor="middle" fontSize="11" fill="#5DBE3A">balanced range</text>

          <text x="240" y="38" textAnchor="middle" fontSize="14" fontWeight="500" fill="#D8DEE6">protein</text>
          <text x="58" y="379" textAnchor="middle" fontSize="14" fontWeight="500" fill="#D8DEE6">carbs</text>
          <text x="422" y="379" textAnchor="middle" fontSize="14" fontWeight="500" fill="#D8DEE6">fat</text>

          {foods.map((f) => (
            <React.Fragment key={f.label}>
              <circle cx={f.x} cy={f.y} r="5" fill="#8b93a0" />
              <text x={f.x + (f.dx ?? 6)} y={f.y + (f.dy ?? -2)} textAnchor={f.anchor ?? "start"} fontSize="11" fill="#AEB7C2">
                {f.label}
              </text>
            </React.Fragment>
          ))}

          <circle cx="184" cy="264" r="10" fill="#5DBE3A" stroke="#F5F0E8" strokeWidth="2.5" />
          <text x="174" y="252" textAnchor="end" fontSize="14" fontWeight="600" fill="#F5F0E8">TrueNooch</text>
        </svg>

        <p style={{ textAlign: "center", fontSize: 12.5, color: "#95A0AE", margin: "8px auto 0", maxWidth: 440 }}>
          Positioned by calorie-share. The balanced range is the Dietary Guidelines&rsquo; healthy split.
        </p>
      </div>

      <div
        style={{
          maxWidth: "52ch",
          margin: "1.5rem auto 0",
          padding: "16px 20px",
          background: "rgba(93, 190, 58, 0.10)",
          border: "1px solid rgba(93, 190, 58, 0.35)",
          borderRadius: 12,
          fontSize: 15.5,
          lineHeight: 1.6,
          color: "#EAF3E2",
        }}
      >
        <strong style={{ color: "#8FD86C" }}>Balanced isn&rsquo;t equal thirds.</strong> It&rsquo;s a healthy
        spread — the U.S. Dietary Guidelines put it at roughly 45–65% of calories from carbs, 20–35% from
        fat, and 10–35% from protein. Most single foods sit well outside that range. TrueNooch lands
        inside it, on the lean, low-fat side.
      </div>

      <p style={{ maxWidth: "52ch", margin: "1.25rem auto 0" }}>
        That&rsquo;s the whole idea behind balanced macros without the mental overhead: you don&rsquo;t
        have to stack three foods and track them to eat balanced — one real food gets you there.
      </p>

      <SectionLink to="/nutrition">See the full nutrition</SectionLink>

      <div className="macro-disclaimer">
        % of calories from each macronutrient · comparison foods are typical published values
      </div>
    </section>
  );
}
